import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import type { TableTennisFixture, TableTennisSet } from '@ps/web-shared-generated-graphql/sports';
import { isEmpty } from 'lodash';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  tableTennisFixture?: TableTennisFixture;
}

const isTableTennisSet = (
  tableTennisSet: TableTennisSet | null
): tableTennisSet is TableTennisSet => !isEmpty(tableTennisSet);

const getPreviousSets = (previousSets: TableTennisFixture['previousSets']): TableTennisSet[] =>
  previousSets?.filter(isTableTennisSet) ?? [];

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: TableTennisFixture['setsWon'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const Tennis: React.FC<Props> = ({
  enrichedParticipants,
  tableTennisFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];
  const currentSet = tableTennisFixture?.currentSet;
  const currentSetScore = currentSet?.score;
  const setsWon = tableTennisFixture?.setsWon;
  const previousSets = tableTennisFixture?.previousSets;
  const currentServingParticipant = currentSet?.currentServer;

  const hasPreviousSets = !isEmpty(previousSets);
  const sets = getPreviousSets(previousSets);

  return (
    <ScoreboardWrapper
      eventId={eventId}
      eventTime={eventTime}
      eventTypeId={EventTypeId.TABLE_TENNIS}
    >
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant
            name={participantA.participant}
            isVisibleMarker={currentServingParticipant === participantA.type}
          />
          <ScoreboardParticipant
            name={participantB.participant}
            isVisibleMarker={currentServingParticipant === participantB.type}
          />
        </ScoreboardParticipants>
        <ScoreboardScores>
          <ScoreboardScore
            isHighlighted
            score={[
              getParticipantScore(participantA, setsWon) ?? null,
              getParticipantScore(participantB, setsWon) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.TABLE_TENNIS, [
              setsWon?.home ?? 0,
              setsWon?.away ?? 0
            ])}
          />
          {hasPreviousSets
            ? sets.map((set) => (
                <ScoreboardScore
                  key={set.number}
                  score={[
                    getParticipantScore(participantA, set.score) ?? null,
                    getParticipantScore(participantB, set.score) ?? null
                  ]}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentSetScore) ?? null,
              getParticipantScore(participantB, currentSetScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.TABLE_TENNIS, [
              currentSetScore?.home ?? 0,
              currentSetScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Tennis;
